import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, CardBody, Card, Button } from 'reactstrap';
import Select from 'react-select';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification, successNofication } from 'src/shared/components/Notification';
import { formatDate } from 'src/utils/DateUtils';
import ConfirmationDialog from 'src/shared/components/ConfirmationDialog';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import PageHeader from 'src/shared/components/layout/PageHeader';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import { Box, LinearProgress } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Badge from 'react-bootstrap/Badge';

const OrderTransferDetailsView = () => {
  useEffect(() => {
    getAllRidersDetails();
    GetAllFreeRiderDetails();
  }, [])

  const [riderIds, setRiderIds] = useState([]);
  const [freeRiderIds, setFreeRiderIds] = useState([]);
  const [reactTableData, setReactTableData] = useState({ tableRowsData: [] });
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [fromRider, setFromRider] = useState(null);
  const [toRider, setToRider] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isConfirtmation, setShowConfirmation] = useState(false);
  const [noFreeRiders, setNoFreeRiders] = useState(false);

  const GetAllFreeRiderDetails = async () => {
    try {
      let freeRiders = [];
      const response = await PndRestApi.GetAllFreeRidersDetailsForOrderTransfer();
      if (response && response.status === 200 && response.data.status === 200) {
        response.data.data.forEach(item => {
          freeRiders.push({
            label: item.riderName,
            value: item.riderId,
            vehicleId: item.vehicleId,
            riderStatusId: item.riderStatusId
          });
        });
        setFreeRiderIds(freeRiders);
        setNoFreeRiders(false);
      } else if (response && response.status === 200 && response.data.status === 204) {
        setFreeRiderIds([]);
        setNoFreeRiders(true);
      } else {
        setNoFreeRiders(false);
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response?.data?.status || response?.status}`,
          `Message:${response?.data?.message || 'Error fetching free riders'}`,
        );
      }
    } catch (error) {
      console.error('Error fetching free riders:', error);
    }
  };

  const getAllRidersDetails = async () => {
    try {
      let riders = [];
      const response = await PndRestApi.GetAllRidersDetails();
      if (response.status === 200 && response.data.status === 200) {
        response.data.data.forEach(item => {
          riders.push({ label: item.riderName, value: item.riderId });
        });
        setRiderIds(riders);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data.message}`,
        );
      }
    } catch (error) {
      console.error('Error fetching riders:', error);
    }
  };

  const getOrders = async (selectedRider) => {
    if (!selectedRider) {
      setReactTableData({ tableRowsData: [] });
      setSelectedOrder(null);
      return;
    }
    try {
      setLoading(true);
      const response = await PndRestApi.GetAssignedOrderByRiderId(selectedRider.value);
      if (response.status === 200 && response.data.status === 200) {
        const orders = response.data.data || [];
        setReactTableData({ tableRowsData: orders });
        if (orders.length > 0) {
          setSelectedOrder(orders[0]);
        } else {
          setSelectedOrder(null);
        }
      } else if (response.status === 200 && response.data.status === 204) {
        setReactTableData({ tableRowsData: [] });
        setSelectedOrder(null);

      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data.message}`,
        );
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const tableRowsData = reactTableData?.tableRowsData || [];
      const orderIds = tableRowsData.map(row => row.orderId);
      let Obj = {
        "orderIds": orderIds,
        "oldRiderId": fromRider.value,
        "newRiderId": toRider.value,
        "newRiderVehicleId": toRider.vehicleId,
        "newRiderStatusId": toRider.riderStatusId
      };

      const response = await PndRestApi.ProcessOrderTransfer(Obj);
      if (response.status === 200 && response.data.status === 200) {
        successNofication(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          ERROR_MESSAGES.successMessage,
        );
        // Reset states to default
        setFromRider(null);
        setToRider(null);
        setReactTableData({ tableRowsData: [] });
        setSelectedOrder(null);
        await GetAllFreeRiderDetails();
        await getAllRidersDetails();
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data.message}`,
        );
      }
    } catch (error) {
      console.error("Error processing order transfer:", error);
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const ORDER_COLUMNS = useMemo(() => [
    {
      key: 'orderNo',
      header: 'Order No.',
      sortable: true,
    },
    {
      key: 'erpOrderNo',
      header: 'ERP Order No.',
      sortable: true,
    },
    {
      key: 'orderStatusInfo',
      header: 'Status',
      sortable: true,
      render: (value) => value?.orderStatusDes || '—'
    }
  ], []);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <PageHeader
        icon={<SwapHorizIcon fontSize="large" />}
        title="Order Transfer Details"
        subtitle="Transfer assigned orders between active riders."
        gradient="linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)"
      />

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      <Row style={{ marginTop: '20px' }}>
        {/* Left Panel: Controls and Table */}
        <Col md={7} lg={7}>
          {/* Controls Bar */}
          <Card className="card--not-full-height" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px', padding: '16px' }}>
            <Row className="align-items-start">
              <Col md={6} sm={12}>
                <div style={{ marginBottom: '8px' }}>
                  <span className="small text-muted fw-bold">Order Transfer From</span>
                  <Select
                    value={fromRider}
                    options={riderIds}
                    placeholder="Select Rider"
                    onChange={(val) => {
                      setFromRider(val);
                      getOrders(val);
                      setToRider(null);
                    }}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: '8px',
                        borderColor: '#cbd5e1',
                        minHeight: '38px',
                      })
                    }}
                  />
                </div>
              </Col>
              {reactTableData?.tableRowsData && reactTableData.tableRowsData.length > 0 && (
                <Col md={6} sm={12}>
                  <div style={{ marginBottom: '8px' }}>
                    <span className="small text-muted fw-bold">Order Transfer To</span>
                    <Select
                      value={toRider}
                      options={freeRiderIds.filter(rider => rider.value !== fromRider?.value)}
                      placeholder="Select Free Rider"
                      onChange={setToRider}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          borderRadius: '8px',
                          borderColor: '#cbd5e1',
                          minHeight: '38px',
                        })
                      }}
                    />
                  </div>
                </Col>
              )}
            </Row>

            {noFreeRiders && (
              <Row style={{ marginTop: '8px' }}>
                <Col md={12}>
                  <span className="text-danger small d-block fw-semibold" style={{ fontSize: '0.78rem' }}>
                    ⚠️ No riders are available at this moment to transfer the orders.
                  </span>
                </Col>
              </Row>
            )}

            {reactTableData?.tableRowsData && reactTableData.tableRowsData.length > 0 && (
              <Row style={{ marginTop: '12px' }}>
                <Col md={12} className="d-flex justify-content-end">
                  <Button
                    color="primary"
                    disabled={!toRider}
                    onClick={() => setShowConfirmation(true)}
                    style={{ borderRadius: '8px', padding: '8px 24px', fontWeight: 600 }}
                  >
                    Transfer Orders
                  </Button>
                </Col>
              </Row>
            )}
          </Card>

          {/* Table */}
          {reactTableData.tableRowsData && reactTableData.tableRowsData.length > 0 ? (
            <RadioSelectTable
              rows={reactTableData.tableRowsData}
              columns={ORDER_COLUMNS}
              rowKey="orderId"
              selectedKey={selectedOrder?.orderId}
              onSelect={setSelectedOrder}
              searchKeys={['orderNo', 'invoiceNo']}
              searchPlaceholder="Search assigned orders..."
              pageSizes={[5, 10, 20]}
            />
          ) : (
            <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center' }}>
              <p className="text-muted m-0">
                {fromRider ? 'No orders assigned to this rider.' : 'Please select a transfer-from rider.'}
              </p>
            </Card>
          )}
        </Col>

        {/* Right Panel: Detailed Order Card */}
        <Col md={5} lg={5}>
          <Card className="card--not-full-height" style={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            minHeight: '520px',
            background: '#ffffff',
            overflow: 'hidden',
          }}>
            {selectedOrder ? (
              <>
                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
                  padding: '24px 20px',
                  color: '#ffffff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                        {selectedOrder.orderNo}
                      </h4>
                      <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                        Invoice: {selectedOrder.invoiceNo || '—'}
                      </span>
                    </div>
                    <div>
                      <span
                        className="badge bg-white text-primary"
                        style={{ fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '12px' }}
                      >
                        {selectedOrder.orderStatusInfo?.orderStatusDes || 'ASSIGNED'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* General Info */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        General Info
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Order Type:</span>
                          <span className="fw-bold small">
                            {selectedOrder.orderType === 1 ? (
                              <Badge bg="success">DROP OFF</Badge>
                            ) : selectedOrder.orderType === 2 ? (
                              <Badge bg="primary">PICK UP</Badge>
                            ) : (
                              <Badge bg="warning">PnD</Badge>
                            )}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Scheduled Date:</span>
                          <span className="fw-bold small text-dark">
                            {selectedOrder.scheduledDateTimeSlot ? formatDate(selectedOrder.scheduledDateTimeSlot) : '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Merchant & Customer Info */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Merchant & Client Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Merchant:</span>
                          <span className="fw-bold small text-dark">{selectedOrder.merchantInfo?.merchantName || '—'}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Client:</span>
                          <span className="fw-bold small text-dark">{selectedOrder.customerInfo?.clientName || '—'}</span>
                        </div>
                      </div>
                    </div>

                  </div>
                </CardBody>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
              }}>
                <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>📦</div>
                <h5 className="fw-bold text-muted">No Order Selected</h5>
                <p className="text-muted small">Select an order from the list to view detailed information.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {isConfirtmation && (
        <ConfirmationDialog
          open={isConfirtmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={handleSubmit}
          title="Confirmation"
          message={`Are you sure you want to transfer all orders from ${fromRider?.label} to ${toRider?.label}?`}
        />
      )}
    </Box>
  );
};

export default OrderTransferDetailsView;